import { useState } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import { saveAsTemplate } from '../../utils/templateUtils';
import { BackButton } from '../ui';

interface SaveToLibraryViewProps {
    onBack: () => void;
}

/**
 * View that handles saving the current plan (with topics + schedule) to the Zorva library.
 * Renders a confirmation UI showing what will be bundled.
 */
export const SaveToLibraryView = ({ onBack }: SaveToLibraryViewProps) => {
    const { plans, topics, schedules, selectedPlanId } = useSafetyTrainingContext();
    const { companyUser, companyOwner, businessAdmin } = useUserContext();

    const [isSaving, setIsSaving] = useState(false);
    const [result, setResult] = useState<{ templateId: string; topicTemplateCount: number } | null>(null);
    const [error, setError] = useState('');

    const selectedPlan = plans.find(p => p.id === selectedPlanId) ?? plans[0] ?? null;
    const planTopics = topics.filter(t => t.planIds?.includes(selectedPlan?.id || ''));
    const planSchedules = schedules.filter(s => s.planId === selectedPlan?.id);

    const handleSave = async () => {
        if (!selectedPlan) return;
        setIsSaving(true);
        setError('');
        try {
            const adminId = businessAdmin?.id || companyUser?.id || companyOwner?.id || '';
            const adminName = businessAdmin?.name || companyUser?.name || companyOwner?.name || 'Admin';

            const res = await saveAsTemplate({
                plan: selectedPlan,
                topics,
                schedules: planSchedules,
                adminId,
                adminName,
            });
            setResult(res);
        } catch (err: any) {
            console.error('Failed to save template:', err);
            setError(err.message || 'Failed to save template. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!selectedPlan) {
        return (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#64748b' }}>
                <p>No plan selected. Go back and select a plan first.</p>
                <BackButton onClick={onBack} buttonText="Back to Dashboard" buttonClasses="back-button--inline" />
            </div>
        );
    }

    if (result) {
        return (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
                    Template saved successfully
                </p>
                <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: 24 }}>
                    "{selectedPlan.name}" has been saved to the Zorva Library with {result.topicTemplateCount} topics.
                    You can now assign it to companies from the Resource Library.
                </p>
                <BackButton onClick={onBack} buttonText="Back to Dashboard" />
            </div>
        );
    }

    return (
        <div style={{ padding: '32px 24px', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#1e293b', marginBottom: 8 }}>Save Plan to Zorva Library</h2>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
                This will package the plan, its topics, and schedule structure into a reusable template
                that can be shared with any company.
            </p>

            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, marginBottom: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>What will be saved:</div>
                <ul style={{ margin: 0, padding: '0 0 0 20px', color: '#475569', fontSize: 14, lineHeight: 1.8 }}>
                    <li>Plan: <strong>{selectedPlan.name}</strong> ({selectedPlan.governingStandard})</li>
                    <li>{planTopics.length} training topics (with content references)</li>
                    <li>{planSchedules.length} schedule entries (day positions)</li>
                    <li>Cycle: {selectedPlan.cycleDuration} days, {selectedPlan.minimumTimeRequired} min required</li>
                </ul>
            </div>

            {error && (
                <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
                <button className="adv-btn-manage" onClick={onBack}>
                    Cancel
                </button>
                <button
                    className="adv-btn-new-action"
                    style={{ background: '#4f46e5' }}
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : '📤 Save as Template'}
                </button>
            </div>
        </div>
    );
};
